import { z } from "zod";

export const PropertyEnrichmentStatusEnum = z
  .enum(["pending", "enriched", "not_found", "error"])
  .describe("Enrichment Status");

export const PropertyLotSizeSourceEnum = z
  .enum(["assessor", "gis_calculated", "measured"])
  .describe("Lot Size Source");

export const PropertyTypeEnum = z
  .enum(["residential", "commercial", "hoa", "vacant_land"])
  .describe("Property Type");

// ADV-909: properties are account-scoped — business_id column dropped.
export const PropertySchema = z.object({
  id: z.string().uuid().describe("ID"),
  location_id: z.string().uuid().describe("Location"),
  account_id: z.string().uuid().describe("Account"),

  // Parcel identity
  apn: z.string().nullable().describe("Assessor Parcel Number"),
  fips_code: z.string().nullable().describe("FIPS Code"),
  parcel_id_source: z.string().nullable().describe("Parcel ID Source"),

  // Lot dimensions
  lot_size_sqft: z.number().nullable().describe("Lot Size (sqft)"),
  lot_size_acres: z.number().nullable().describe("Lot Size (acres)"),
  lot_size_source: z.string().nullable().describe("Lot Size Source"),

  // Aerial measurement
  turfable_area_sqft: z.number().nullable().describe("Turfable Area (sqft)"),
  impervious_area_sqft: z
    .number()
    .nullable()
    .describe("Impervious Area (sqft)"),
  tree_canopy_sqft: z.number().nullable().describe("Tree Canopy (sqft)"),
  aerial_measured_at: z.string().nullable().describe("Aerial Measured At"),
  aerial_provider: z.string().nullable().describe("Aerial Provider"),

  // Structure
  building_sqft: z.number().nullable().describe("Building Size (sqft)"),
  building_footprint_sqft: z
    .number()
    .nullable()
    .describe("Building Footprint (sqft)"),
  year_built: z.number().nullable().describe("Year Built"),
  stories: z.number().nullable().describe("Stories"),

  // Classification
  property_type: z.string().nullable().describe("Property Type"),
  land_use_code: z.string().nullable().describe("Land Use Code"),
  land_use_desc: z.string().nullable().describe("Land Use Description"),
  zoning_code: z.string().nullable().describe("Zoning Code"),
  zoning_desc: z.string().nullable().describe("Zoning Description"),

  // Owner
  owner_name: z.string().nullable().describe("Owner Name"),
  owner_occupied: z.boolean().nullable().describe("Owner Occupied"),

  // Parcel geometry (GeoJSON)
  parcel_boundary: z.record(z.unknown()).nullable().describe("Parcel Boundary"),

  // Enrichment provenance
  enrichment_provider: z.string().nullable().describe("Enrichment Provider"),
  enrichment_fetched_at: z.string().nullable().describe("Enriched At"),
  enrichment_status: z.string().nullable().describe("Enrichment Status"),
  raw_provider_data: z
    .record(z.unknown())
    .nullable()
    .describe("Raw Provider Data"),

  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
});
export type Property = z.infer<typeof PropertySchema>;

export const PropertyInsertSchema = z.object({
  location_id: z.string().uuid().describe("Location"),
  lot_size_sqft: z.number().optional().describe("Lot Size (sqft)"),
  lot_size_acres: z.number().optional().describe("Lot Size (acres)"),
  lot_size_source: z.string().optional().describe("Lot Size Source"),
  property_type: z.string().optional().describe("Property Type"),
  apn: z.string().optional().describe("Assessor Parcel Number"),
  fips_code: z.string().optional().describe("FIPS Code"),
  enrichment_provider: z.string().optional().describe("Enrichment Provider"),
  enrichment_status: z.string().optional().describe("Enrichment Status"),
  raw_provider_data: z
    .record(z.unknown())
    .optional()
    .describe("Raw Provider Data"),
});

export const PropertyUpdateSchema = PropertyInsertSchema.partial();

export const PropertyQuerySchema = z.object({
  property_type: z.string().optional(),
  enrichment_status: z.string().optional(),
  lot_size_min: z.coerce.number().optional(),
  lot_size_max: z.coerce.number().optional(),
});
